import React, { useState, useEffect, useRef } from 'react'
import BookRating, { BookRatingDisplay } from './BookRating.jsx'
import * as api from '../../services/api.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import AppSelect from '../../components/AppSelect.jsx'

const STATUS_ORDER  = ['reading', 'completed', 'wishlist', 'abandoned']
const STATUS_LABELS = { reading: 'Reading', completed: 'Completed', abandoned: 'Abandoned', wishlist: 'Wishlist' }
const TYPE_LABELS   = { physical: 'Physical', audiobook: 'Audiobook', ebook: 'E-book' }
const FEELING_LABELS = { hard: '😣 Hard', okay: '😐 Okay', good: '🙂 Good', calm: '🌿 Calm' }

const GENRE_GROUPS = [
  { name: 'Fantasy',        subs: ['Epic Fantasy', 'High Fantasy', 'Urban Fantasy', 'Dark Fantasy', 'LitRPG', 'Progression Fantasy', 'Cozy Fantasy', 'Grimdark'] },
  { name: 'Science Fiction', subs: ['Hard Sci-Fi', 'Space Opera', 'Cyberpunk', 'Dystopian', 'Time Travel', 'Military Sci-Fi', 'Solarpunk'] },
  { name: 'Mystery',        subs: ['Cozy Mystery', 'Crime', 'Detective', 'Nordic Noir', 'Historical Mystery', 'Heist'] },
  { name: 'Thriller',       subs: ['Psychological Thriller', 'Legal Thriller', 'Spy Thriller', 'Medical Thriller', 'Political Thriller'] },
  { name: 'Romance',        subs: ['Contemporary Romance', 'Historical Romance', 'Paranormal Romance', 'Romantic Suspense', 'Dark Romance'] },
  { name: 'Horror',         subs: ['Gothic Horror', 'Supernatural', 'Psychological Horror', 'Cosmic Horror'] },
  { name: 'Historical',     subs: ['Ancient World', 'Medieval', 'Tudor', 'Victorian', 'World War', '20th Century'] },
  { name: 'Literary Fiction', subs: ['Contemporary Fiction', 'Coming of Age', 'Magical Realism'] },
  { name: 'Non-Fiction',    subs: ['Memoir', 'Biography', 'True Crime', 'History', 'Popular Science', 'Self-Help', 'Essay Collection', 'Travel'] },
  { name: 'Young Adult',    subs: ['YA Fantasy', 'YA Romance', 'YA Dystopian', 'YA Contemporary'] },
  { name: 'Manga & Comics', subs: ['Manga', 'Manhwa', 'Graphic Novel'] },
  { name: 'Poetry',         subs: [] },
  { name: 'Short Stories',  subs: [] },
  { name: 'Children\'s',    subs: ['Picture Book', 'Middle Grade'] },
]

export function GenrePicker({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState({})
  const ref = useRef(null)

  const selected = value
    ? value.split(',').map(s => s.trim()).filter(Boolean)
    : []

  useEffect(() => {
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open])

  function toggle(genre) {
    const next = selected.includes(genre)
      ? selected.filter(g => g !== genre)
      : [...selected, genre]
    onChange(next.join(', '))
  }

  function toggleGroup(name) {
    setExpanded(e => ({ ...e, [name]: !e[name] }))
  }

  return (
    <div className="genre-picker-wrap" ref={ref}>
      <button
        type="button"
        className="genre-picker-btn"
        onClick={() => setOpen(o => !o)}
      >
        {selected.length === 0
          ? 'Select genres…'
          : selected.length <= 3
            ? selected.join(', ')
            : `${selected.slice(0, 3).join(', ')} +${selected.length - 3}`}
        <span className="genre-picker-caret">{open ? '▴' : '▾'}</span>
      </button>

      {selected.length > 0 && (
        <div className="genre-chips-row">
          {selected.map(g => (
            <span key={g} className="genre-chip selected" onClick={() => toggle(g)}>
              {g} ✕
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="genre-picker-panel">
          {GENRE_GROUPS.map(group => (
            <div key={group.name} className="genre-group">
              <div
                className="genre-group-header"
                onClick={() => group.subs.length ? toggleGroup(group.name) : toggle(group.name)}
              >
                <span
                  className={`genre-group-name${selected.includes(group.name) ? ' selected' : ''}`}
                  onClick={e => { e.stopPropagation(); toggle(group.name) }}
                >
                  {group.name}
                </span>
                {group.subs.length > 0 && (
                  <span className="genre-group-expand">{expanded[group.name] ? '▴' : '▾'}</span>
                )}
              </div>
              {group.subs.length > 0 && expanded[group.name] && (
                <div className="genre-subs">
                  {group.subs.map(sub => (
                    <span
                      key={sub}
                      className={`genre-chip${selected.includes(sub) ? ' selected' : ''}`}
                      onClick={() => toggle(sub)}
                    >
                      {sub}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const FIELD = {
  width: '100%', boxSizing: 'border-box',
  padding: '6px 9px', fontSize: '0.85rem',
  border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8,
  background: 'var(--bg)', color: 'var(--ink)',
  fontFamily: 'var(--font-sans)',
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function BookDetailPanel({ book, onClose, onLogSession, onUpdateBook, onDeleteBook }) {
  const [form,     setForm]     = useState(null)
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem('reading-panel-expanded') || 'false') }
    catch { return false }
  })
  const [sessions, setSessions] = useState([])
  const [open,              setOpen]              = useState(false)
  const [hasChanges,        setHasChanges]        = useState(false)
  const [savedMsg,          setSavedMsg]          = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (!book) { setOpen(false); return }
    setForm({
      title:    book.title      || '',
      author:   book.author     || '',
      status:   book.status     || 'reading',
      type:     book.type       || 'physical',
      pages:    book.pages      != null ? String(book.pages) : '',
      pagesRead:book.pages_read != null ? String(book.pages_read) : '',
      rating:   book.rating     || 0,
      series:   book.series     || '',
      continues:book.continues  || 0,
      genres:   book.genres     || '',
      date:     book.date       || '',
      hours:    book.hours      != null ? String(book.hours) : '',
      review:   book.review     || '',
    })
    setHasChanges(false)
    setSessions([])
    api.getReadingSessions(book.id).then(r => setSessions(r || []))
    setTimeout(() => setOpen(true), 10)
  }, [book?.id])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  function handleClose() {
    setOpen(false)
    setTimeout(onClose, 260)
  }

  function toggleExpanded() {
    const next = !expanded
    setExpanded(next)
    localStorage.setItem('reading-panel-expanded', JSON.stringify(next))
  }

  function setField(key, val) {
    setForm(f => ({ ...f, [key]: val }))
    setHasChanges(true)
  }

  function saveChanges() {
    if (!form) return
    const updates = {
      title:      form.title.trim(),
      author:     form.author.trim()   || null,
      status:     form.status,
      type:       form.type,
      pages:      parseInt(form.pages)    || 0,
      pages_read: parseInt(form.pagesRead) || 0,
      rating:     form.rating,
      series:     form.series.trim()   || null,
      continues:  form.continues,
      genres:     form.genres.trim()   || null,
      date:       form.date            || null,
      hours:      parseFloat(form.hours) || null,
      review:     form.review.trim()   || null,
    }
    onUpdateBook(book.id, updates)
    setHasChanges(false)
    setSavedMsg(true)
    setTimeout(() => setSavedMsg(false), 2000)
  }

  if (!book) return null

  const progressPct = form?.pages > 0
    ? Math.min(Math.round(((parseInt(form.pagesRead) || 0) / parseInt(form.pages)) * 100), 100)
    : 0

  const seriesHint = form?.series && form?.continues
    ? `Next in the "${form.series}" series`
    : null

  return (
    <>
      <div
        className="book-panel-backdrop"
        onClick={handleClose}
        style={{ opacity: open ? 1 : 0, transition: 'opacity 0.25s' }}
      />
      <div className={`book-detail-panel${open ? ' open' : ''}`}>
        {form && (
          <>
            {/* Close */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.25rem' }}>
              <button className="panel-close-btn" onClick={handleClose} aria-label="Close">✕</button>
            </div>

            {/* Color stripe + title / author */}
            <div className="book-panel-header" style={{ marginBottom: '0.9rem' }}>
              <div style={{
                width: 5, borderRadius: 3, flexShrink: 0, minHeight: 52,
                background: book.color || 'var(--accent)'
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <input
                  className="panel-title-input"
                  value={form.title}
                  onChange={e => setField('title', e.target.value)}
                  placeholder="Book title"
                />
                <input
                  className="panel-author-input"
                  value={form.author}
                  onChange={e => setField('author', e.target.value)}
                  placeholder="Author"
                />
              </div>
            </div>

            {/* Series hint */}
            {seriesHint && (
              <div className="series-hint">
                <span>📚</span> {seriesHint}
              </div>
            )}

            {/* Status + type */}
            <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>
              <div style={{ flex: 1 }}>
                <AppSelect
                  value={form.status}
                  onChange={v => setField('status', v)}
                  options={STATUS_ORDER.map(s => ({ value: s, label: STATUS_LABELS[s] }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <AppSelect
                  value={form.type}
                  onChange={v => setField('type', v)}
                  options={Object.entries(TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                />
              </div>
            </div>

            {/* Pages + progress */}
            {form.type !== 'audiobook' && (
              <div style={{ marginBottom: '0.9rem' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: '0.5rem' }}>
                  <div style={{ flex: 1 }}>
                    <div className="panel-field-label">Pages</div>
                    <input
                      type="number" min="0"
                      value={form.pages}
                      onChange={e => setField('pages', e.target.value)}
                      style={FIELD}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="panel-field-label">Pages read</div>
                    <input
                      type="number" min="0"
                      value={form.pagesRead}
                      onChange={e => setField('pagesRead', e.target.value)}
                      style={FIELD}
                    />
                  </div>
                </div>
                {parseInt(form.pages) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 4, background: 'rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: 'var(--accent)', width: `${progressPct}%`, borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--stone)', flexShrink: 0 }}>{progressPct}%</span>
                  </div>
                )}
              </div>
            )}

            {/* Rating */}
            <div style={{ marginBottom: '0.9rem' }}>
              <div className="panel-field-label">Rating</div>
              <BookRating value={form.rating} onChange={r => setField('rating', r)} />
            </div>

            {/* More details toggle */}
            <button className="book-panel-expand-btn" onClick={toggleExpanded}>
              {expanded ? '▴ Less details' : '▾ More details'}
            </button>

            {expanded && (
              <div className="book-panel-details" style={{ marginBottom: '0.9rem' }}>
                {/* Series */}
                <div style={{ display: 'flex', gap: 8, marginBottom: '0.6rem', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div className="panel-field-label">Series</div>
                    <input
                      value={form.series}
                      onChange={e => setField('series', e.target.value)}
                      placeholder="Series name"
                      style={FIELD}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', color: 'var(--stone)', cursor: 'pointer', paddingBottom: '7px', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={!!form.continues}
                      onChange={e => setField('continues', e.target.checked ? 1 : 0)}
                    />
                    Want to continue
                  </label>
                </div>

                {/* Genres */}
                <div style={{ marginBottom: '0.6rem' }}>
                  <div className="panel-field-label">Genres</div>
                  <GenrePicker value={form.genres} onChange={v => setField('genres', v)} />
                </div>

                {/* Date + Hours */}
                <div style={{ display: 'flex', gap: 8, marginBottom: '0.6rem' }}>
                  <div style={{ flex: 1 }}>
                    <div className="panel-field-label">Finish date</div>
                    <input
                      type="date"
                      value={form.date}
                      onChange={e => setField('date', e.target.value)}
                      style={FIELD}
                    />
                  </div>
                  {form.type === 'audiobook' && (
                    <div style={{ flex: 1 }}>
                      <div className="panel-field-label">Hours</div>
                      <input
                        type="number" step="0.5" min="0"
                        value={form.hours}
                        onChange={e => setField('hours', e.target.value)}
                        style={FIELD}
                      />
                    </div>
                  )}
                </div>

                {/* Review */}
                <div>
                  <div className="panel-field-label">Review / notes</div>
                  <textarea
                    value={form.review}
                    onChange={e => setField('review', e.target.value)}
                    placeholder="Your thoughts..."
                    rows={3}
                    style={{ ...FIELD, resize: 'vertical', fontFamily: 'var(--font-serif)', lineHeight: 1.65 }}
                  />
                </div>
              </div>
            )}

            {/* Log session button */}
            <button
              className="panel-session-btn"
              onClick={onLogSession}
              style={{ width: '100%', marginBottom: '1rem' }}
            >
              + Log reading session
            </button>

            {/* Recent sessions */}
            {sessions.length > 0 && (
              <>
                <div className="book-panel-section-label">Recent sessions</div>
                <div style={{ marginBottom: '1rem' }}>
                  {sessions.slice(0, 3).map((s, i) => (
                    <div key={s.id || i} className="session-row">
                      <span className="session-feeling">{FEELING_LABELS[s.feeling] || s.feeling}</span>
                      <span style={{ flex: 1 }} />
                      {s.progress_value > 0 && (
                        <span style={{ fontSize: '0.68rem', color: 'var(--stone)' }}>
                          +{s.progress_value} {s.progress_unit || ''}
                        </span>
                      )}
                      <span style={{ fontSize: '0.68rem', color: 'var(--stone)' }}>
                        {s.date || formatDate(s.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Delete */}
            <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '0.9rem' }}>
              <button
                style={{
                  fontSize: '0.72rem', color: '#DC2626', background: 'transparent',
                  border: '1px solid #DC2626', borderRadius: 8, padding: '5px 12px',
                  cursor: 'pointer', fontFamily: 'var(--font-sans)',
                }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete book
              </button>
            </div>

            {/* Save area — sticky at bottom */}
            <div className="panel-save-area">
              <button
                className={`panel-save-btn${savedMsg ? ' saved' : ''}`}
                onClick={saveChanges}
                disabled={!hasChanges && !savedMsg}
              >
                {savedMsg ? 'Saved ✓' : 'Save changes'}
              </button>
              {(hasChanges || savedMsg) && (
                <span className="panel-save-hint">
                  {savedMsg ? 'All saved' : 'Unsaved changes'}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete this book?"
        message="All sessions for this book will also be removed. This cannot be undone."
        onConfirm={() => { setShowDeleteConfirm(false); onDeleteBook?.(book.id); handleClose() }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  )
}
